import * as THREE from 'three';
import { BLACK, DSTEEL, DWOOD, STRING, WOOD, cloth, emis, metal, std, stone } from '../materials.ts';
import { G, M, V, flame, glowSprite, limb, ring } from '../prims.ts';
import { mulberry32 } from '../rand.ts';
import type { Era2Material } from './era2.ts';
import { ASHLAR, IRON_BAND, IRON_PLATE, IRON_RIVET, IRON_STONE, crack, crewman, rubbleAround, snapTop, splinters, stain, type Level, type Wear } from './structures.ts';
import { begin, done, part, type BaseOpts, type Model } from './types.ts';

/*
 * Era IV, the Vigil's bastion: the ranger's watchtower, the cleric's sanctum and the trebuchet. The era's look
 * carries over from the crowned keep: the Vigil's silver-white flame (never gold) and the Order's crimson
 * standards. Each draws its three materials itself (1 timber and fieldstone, 2 dressed stone, 3 stone bound in
 * iron) and its wear (1 worn, 2 crumbling), and leaves its own ruin. Lights are glows only (docs/RENDIMIENTO.md).
 */

export interface Era4Opts extends BaseOpts {
  material?: Era2Material;
  wear?: Wear;
}
export interface TrebuchetOpts extends Era4Opts {
  level?: Level;
}

const TAU = Math.PI * 2;
const VIGIL = '#dfe8ff';
const VIGIL_LIT = emis('#f2f6ff', '#bcd0ff', 1.4);
const SILVER = metal('#c9d0da', { roughness: 0.3 });
const CRIMSON = cloth('#7a1d22');
const EMBLEM = cloth('#d9dce3');
const SHINGLE = std('#3a2f28', { roughness: 0.95, flatShading: true });
const SLATE = stone('#3f4450');
const FEATHER = std('#6b5a4a', { roughness: 0.9 });
const FEATHER_LT = std('#cfc6b8', { roughness: 0.9 });

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number, ry = 0): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  m.rotation.y = ry;
  return m;
};

/** A squared timber from a to b (`w` thick). */
const beam = (parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, w: number, mat: THREE.Material): THREE.Mesh => {
  const m = limb(parent, a, b, w * 0.7, w * 0.7, mat, 4);
  return m;
};

const stoneOf = (mat: Era2Material): THREE.MeshStandardMaterial => (mat === 1 ? stone('#77736c') : mat === 3 ? stone(IRON_STONE) : stone('#a39e94'));
const darkOf = (mat: Era2Material): THREE.MeshStandardMaterial => (mat === 3 ? stone('#4d5157') : stone('#5f5b55'));
const rubbleColors = (mat: Era2Material): string[] => (mat === 1 ? ['#77736c', '#6b675f', '#837e75'] : mat === 3 ? ['#72767c', '#5d6167', '#868a90'] : ASHLAR);

/** The Order's crimson pennant on a pole, its silver-white emblem; `h` the pole's height above `y0`. */
const pennant = (parent: THREE.Object3D, x: number, y0: number, z: number, h: number, ry = 0): void => {
  const pg = G(parent, x, y0, z);
  pg.rotation.y = ry;
  limb(pg, V(0, 0, 0), V(0, h, 0), 0.014, 0.016, DWOOD, 5);
  const flag = box(pg, CRIMSON, 0.22, 0.12, 0.012, 0.12, h - 0.08, 0);
  flag.rotation.y = 0;
  const em = box(pg, EMBLEM, 0.045, 0.045, 0.016, 0.1, h - 0.08, 0);
  em.rotation.z = Math.PI / 4;
  M(new THREE.SphereGeometry(0.02, 6, 4), SILVER, pg).position.y = h + 0.015;
};

/** The Vigil's eye carved in a face and lit silver-white: an almond, its iris, rays; facing +Z of `parent`. */
const vigilEye = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, k: number, dim: number): void => {
  const e = G(parent, x, y, z);
  e.rotation.y = ry;
  const lit = dim > 0.7 ? VIGIL_LIT : emis('#c8cfdc', '#8ea0c8', 1.4 * dim);
  const almond = M(new THREE.TorusGeometry(0.05 * k, 0.007 * k, 3, 14), lit, e);
  almond.scale.set(1.5, 0.75, 1);
  M(new THREE.CircleGeometry(0.02 * k, 8), lit, e).position.z = 0.003;
  for (const a of [-0.5, 0, 0.5]) {
    const r = box(e, lit, 0.006 * k, 0.03 * k, 0.006, Math.sin(a) * 0.065 * k, 0.05 * k + Math.cos(a) * 0.02 * k, 0);
    r.rotation.z = -a;
  }
  glowSprite(e, VIGIL, 0.3 * k * dim, V(0, 0, 0.04), 0.55 * dim);
};

/** A hawk perched with its wings folded, facing +Z; `s` its size. */
const perchedHawk = (parent: THREE.Object3D, x: number, y: number, z: number, ry: number, s: number): void => {
  const h = G(parent, x, y, z);
  h.rotation.y = ry;
  h.scale.setScalar(s);
  const body = M(new THREE.SphereGeometry(0.1, 8, 6), FEATHER, h);
  body.scale.set(0.8, 1.15, 0.85);
  body.position.y = 0.1;
  body.rotation.x = -0.25;
  const chest = M(new THREE.SphereGeometry(0.075, 8, 6), FEATHER_LT, h);
  chest.position.set(0, 0.09, 0.05);
  chest.scale.set(0.8, 1.1, 0.7);
  M(new THREE.SphereGeometry(0.06, 8, 6), FEATHER_LT, h).position.set(0, 0.24, 0.02);
  const beak = M(new THREE.ConeGeometry(0.018, 0.05, 5), BLACK, h);
  beak.position.set(0, 0.23, 0.08);
  beak.rotation.x = Math.PI / 2 + 0.6;
  const tail = M(new THREE.BoxGeometry(0.07, 0.15, 0.015), FEATHER, h);
  tail.position.set(0, -0.02, -0.07);
  tail.rotation.x = 0.35;
  for (const k of [-1, 1]) {
    const w = M(new THREE.BoxGeometry(0.02, 0.17, 0.12), FEATHER, h);
    w.position.set(k * 0.075, 0.1, -0.01);
    w.rotation.x = -0.3;
  }
};

/* ---------- the ranger's watchtower ---------- */

/**
 * The watchtower: a tall slender lookout. Timber legs braced in X on fieldstone footings, a ladder up the front (1);
 * a tapering shaft of dressed stone with arrow slits (2), banded and angle-ironed in iron (3). Up top a railed deck
 * (crenels in stone) under a pyramid roof on four posts: the Vigil's lantern burns silver-white under it, the
 * Order's pennant flies from the peak, the ranger's hawk keeps watch on the parapet and the Vigil's eye is carved on
 * the front. Worn (1): cracks, soot, a rail or a merlon gone. Crumbling (2): a roof post snapped and the roof
 * slumped onto the deck, the lantern nearly dark, the parapet broken, rubble and splinters at the foot.
 */
export const watchtower = (o?: Era4Opts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.7, 410);
  const { g } = c;
  const wr = mulberry32(4100 + mat * 3 + wear);
  const st = stoneOf(mat);
  const dk = darkOf(mat);
  const DECK = 1.08;
  const dim = [1, 0.6, 0.25][wear] as number;
  box(g, dk, 0.78, 0.08, 0.78, 0, 0.04, 0, Math.PI / 4).rotation.y = 0;
  if (mat === 1) {
    // Four timber legs leaning in, braced in X on every face, on fieldstone footings.
    const b0 = 0.3, b1 = 0.21;
    const legs: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    for (const [x, z] of legs) {
      const f = M(new THREE.DodecahedronGeometry(0.09, 0), st, g);
      f.position.set(x * b0, 0.1, z * b0);
      f.scale.set(1, 0.7, 1);
      beam(g, V(x * b0, 0.08, z * b0), V(x * b1, DECK, z * b1), 0.07, WOOD);
    }
    for (let i = 0; i < 4; i++) {
      const [ax, az] = legs[i] as [number, number];
      const [bx, bz] = legs[(i + 1) % 4] as [number, number];
      for (const [y0, y1] of [[0.18, 0.62], [0.62, 1.02]] as const) {
        const r0 = b0 + (b1 - b0) * (y0 / DECK), r1 = b0 + (b1 - b0) * (y1 / DECK);
        if (wear === 2 && i === 1 && y0 > 0.5) {
          // A brace torn loose, hanging from one end.
          beam(g, V(ax * r1, y1, az * r1), V(ax * r1 * 0.6 + bx * r0 * 0.4, y0 + 0.1, az * r1 * 0.6 + bz * r0 * 0.4), 0.03, DWOOD);
          continue;
        }
        beam(g, V(ax * r0, y0, az * r0), V(bx * r1, y1, bz * r1), 0.035, DWOOD);
        beam(g, V(bx * r0, y0, bz * r0), V(ax * r1, y1, az * r1), 0.035, DWOOD);
      }
      // A girt round the legs at mid height.
      const rm = b0 + (b1 - b0) * 0.57;
      beam(g, V(ax * rm, 0.62, az * rm), V(bx * rm, 0.62, bz * rm), 0.04, WOOD);
    }
    // A ladder up the front.
    for (const k of [-1, 1]) beam(g, V(k * 0.09, 0.08, 0.42), V(k * 0.07, DECK + 0.05, 0.25), 0.025, WOOD);
    for (let i = 1; i < 8; i++) {
      const t = i / 8;
      const y = 0.08 + t * (DECK - 0.03);
      const z = 0.42 - t * 0.17;
      box(g, DWOOD, 0.16 - t * 0.04, 0.015, 0.02, 0, y, z);
    }
  } else {
    // A square shaft of dressed stone, tapering, coursed with mortar lines; a door and two slits.
    const shaft = M(new THREE.CylinderGeometry(0.3, 0.4, DECK - 0.08, 4), st, g);
    shaft.position.y = 0.08 + (DECK - 0.08) / 2;
    shaft.rotation.y = Math.PI / 4;
    const mortar = std(mat === 3 ? '#4a4e55' : '#6f6a62');
    for (let i = 1; i < 6; i++) {
      const y = 0.08 + i * 0.17;
      const w = 0.4 - (i * 0.17 * 0.1) / (DECK - 0.08) - 0.001;
      const band = M(new THREE.CylinderGeometry(w * 1.0, w * 1.0, 0.008, 4, 1, true), mortar, g);
      band.position.y = y;
      band.rotation.y = Math.PI / 4;
    }
    const half = (y: number): number => (0.4 - ((y - 0.08) / (DECK - 0.08)) * 0.1) * Math.SQRT1_2;
    box(g, DWOOD, 0.13, 0.22, 0.02, 0, 0.19, half(0.19) + 0.005);
    box(g, dk, 0.16, 0.035, 0.03, 0, 0.315, half(0.31) + 0.008);
    for (const [x, y, ry] of [[0, 0.62, 0], [half(0.75), 0.75, Math.PI / 2], [-half(0.55), 0.55, -Math.PI / 2]] as const) {
      const sl = box(g, BLACK, 0.025, 0.13, 0.012, ry === 0 ? x : x + Math.sign(x) * 0.004, y, ry === 0 ? half(y) + 0.004 : 0, ry);
      sl.position.z = ry === 0 ? half(y) + 0.004 : 0;
    }
    if (mat === 3) {
      // Iron bands round the shaft and angle irons up its corners.
      for (const y of [0.16, 0.56, 0.98]) {
        const w = 0.4 - ((y - 0.08) / (DECK - 0.08)) * 0.1;
        const b = M(new THREE.CylinderGeometry(w * 1.02, w * 1.02, 0.04, 4, 1, true), IRON_BAND, g);
        b.position.y = y;
        b.rotation.y = Math.PI / 4;
      }
      for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        beam(g, V(x * 0.4 * Math.SQRT1_2 * 1.0, 0.1, z * 0.4 * Math.SQRT1_2 * 1.0), V(x * 0.3 * Math.SQRT1_2, DECK - 0.02, z * 0.3 * Math.SQRT1_2), 0.03, IRON_PLATE);
      }
      for (const y of [0.16, 0.56, 0.98]) M(new THREE.OctahedronGeometry(0.012, 0), IRON_RIVET, g).position.set(0, y, half(y) + 0.012);
    }
  }
  // The Vigil's eye on the front, above the door.
  vigilEye(g, 0, mat === 1 ? 0.84 : 0.86, mat === 1 ? 0.235 : (0.4 - ((0.86 - 0.08) / (DECK - 0.08)) * 0.1) * Math.SQRT1_2 + 0.006, 0, mat === 1 ? 0.9 : 1, dim);
  if (mat === 1) {
    // On the ladder face there is no wall: the eye hangs on a carved board between the legs.
    box(g, WOOD, 0.22, 0.14, 0.02, 0, 0.84, 0.225);
  }

  // The deck on corbels, and its parapet.
  const top = G(g, 0, DECK, 0);
  const D = 0.36;
  box(top, mat === 1 ? WOOD : dk, D * 2 + 0.04, 0.06, D * 2 + 0.04, 0, 0.03, 0);
  for (const [x, z] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) box(top, mat === 1 ? DWOOD : st, x ? 0.06 : 0.3, 0.08, z ? 0.06 : 0.3, x * (D - 0.02), -0.04, z * (D - 0.02));
  if (mat === 1) {
    // Timber rail on posts.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU;
      const sx = Math.round(Math.cos(a)), sz = Math.round(Math.sin(a));
      if (wear >= 1 && i === 2) continue;
      for (const t of [-1, 0, 1]) {
        const x = sx ? sx * (D - 0.01) : t * (D - 0.01);
        const z = sz ? sz * (D - 0.01) : t * (D - 0.01);
        limb(top, V(x, 0.06, z), V(x, 0.24, z), 0.015, 0.015, DWOOD, 4);
      }
      const rail = box(top, WOOD, sx ? 0.03 : D * 2, 0.03, sz ? 0.03 : D * 2, sx * (D - 0.01), 0.23, sz * (D - 0.01));
      if (wear === 2 && i === 0) {
        rail.rotation.z = 0.5;
        rail.position.y = 0.15;
      }
    }
  } else {
    // Crenels: three merlons a side.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU;
      const sx = Math.round(Math.cos(a)), sz = Math.round(Math.sin(a));
      box(top, st, sx ? 0.06 : D * 2 + 0.04, 0.07, sz ? 0.06 : D * 2 + 0.04, sx * (D - 0.01), 0.095, sz * (D - 0.01));
      for (const t of [-1, 0, 1]) {
        if (wear === 1 && i === 2 && t === 0) continue;
        if (wear === 2 && i === 0 && t !== -1) continue;
        const x = sx ? sx * (D - 0.01) : t * (D - 0.08);
        const z = sz ? sz * (D - 0.01) : t * (D - 0.08);
        box(top, st, sx ? 0.07 : 0.12, 0.1, sz ? 0.07 : 0.12, x, 0.18, z);
      }
      if (mat === 3) box(top, IRON_BAND, sx ? 0.075 : D * 2 + 0.05, 0.025, sz ? 0.075 : D * 2 + 0.05, sx * (D - 0.01), 0.07, sz * (D - 0.01));
    }
  }

  // Four posts and the pyramid roof, the lantern under it, the pennant from the peak.
  const roof = G(top, 0, 0, 0);
  const P = D - 0.05;
  const posts: [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
  for (const [x, z] of posts) {
    if (wear === 2 && x > 0 && z > 0) {
      limb(top, V(x * P, 0.06, z * P), V(x * P, 0.2, z * P), 0.022, 0.022, DWOOD, 5);
      snapTop(top, x * P, 0.2, z * P, 0.022, DWOOD, wr);
      continue;
    }
    limb(roof, V(x * P, 0.06, z * P), V(x * P, 0.44, z * P), 0.022, 0.022, DWOOD, 5);
  }
  const cone = M(new THREE.ConeGeometry(0.4, 0.36, 4), mat === 1 ? SHINGLE : SLATE, roof);
  cone.position.y = 0.44 + 0.18;
  cone.rotation.y = Math.PI / 4;
  if (mat === 3) {
    const rim = M(new THREE.CylinderGeometry(0.4, 0.4, 0.03, 4, 1, true), IRON_BAND, roof);
    rim.position.y = 0.455;
    rim.rotation.y = Math.PI / 4;
  }
  pennant(roof, 0, 0.8, 0, 0.32);
  if (wear === 2) {
    // The roof slumps toward the snapped post.
    roof.position.set(-P, 0.0, -P);
    roof.rotation.set(0.22, 0, -0.22);
    for (const ch of [...roof.children]) ch.position.add(V(P, 0, P));
  }
  // The lantern: a silver cage, the white flame inside.
  const lg = G(top, 0, 0.3, 0);
  limb(lg, V(0, 0.14, 0), V(0, 0.32, 0), 0.006, 0.006, DSTEEL, 3);
  box(lg, SILVER, 0.11, 0.015, 0.11, 0, 0.12, 0);
  box(lg, SILVER, 0.11, 0.015, 0.11, 0, -0.04, 0);
  for (const [x, z] of posts) limb(lg, V(x * 0.05, -0.04, z * 0.05), V(x * 0.05, 0.12, z * 0.05), 0.005, 0.005, SILVER, 3);
  if (wear < 2) flame(lg, V(0, -0.03, 0), 0.4, wr, VIGIL);
  else M(new THREE.SphereGeometry(0.02, 6, 4), emis('#8a94a8', '#5a6a8a', 0.6), lg).position.y = 0.0;
  glowSprite(lg, VIGIL, 0.9 * dim, V(0, 0.03, 0), 0.75 * dim);

  // The ranger's hawk on the parapet.
  if (wear < 2) perchedHawk(top, D - 0.06, mat === 1 ? 0.245 : 0.23, D - 0.06, Math.PI / 4, 0.8);

  if (wear > 0) {
    if (mat === 1) {
      for (const [x, y, z] of [[0.24, 0.4, 0.24], [-0.24, 0.8, 0.24]] as const) stain(g, x, y, z + 0.03, 0, 0.06, wr, '#241c15');
      splinters(g, wear === 2 ? 9 : 4, 0.55, wr);
    } else {
      for (const [ry, y] of [[0, 0.45], [Math.PI / 2, 0.7], [-Math.PI / 2, 0.3]] as const) {
        const r = (0.4 - ((y - 0.08) / (DECK - 0.08)) * 0.1) * Math.SQRT1_2 + 0.006;
        crack(g, Math.sin(ry) * r + (ry === 0 ? 0.12 : 0), y, Math.cos(ry) * r, ry, wear === 2 ? 0.28 : 0.18, wr);
      }
      stain(g, -0.1, 0.9, (0.3 * Math.SQRT1_2) + 0.008, 0, 0.07, wr);
    }
    rubbleAround(g, wear === 2 ? 10 : 5, 0.58, wr, rubbleColors(mat));
  }
  return done(c, wear === 2 ? 1.95 : 2.05);
};

/* ---------- the cleric's sanctum ---------- */

/**
 * The sanctum: a round shrine on two octagonal steps, a ring of slender columns under a dome, the Vigil's
 * flame on a white altar inside and a silver star on the dome's spire, both lit silver-white. Crimson hangings
 * of the Order between the side columns and a crimson runner up the steps. Timber posts and a shingled dome on
 * fieldstone (1), pale dressed stone and a lead-grey dome (2), iron-ribbed dome and iron rings on the columns (3).
 * Worn (1): cracks and stains, a capital chipped. Crumbling (2): a column broken (its drums on the steps), the
 * spire's star fallen, the flame low, rubble.
 */
export const sanctum = (o?: Era4Opts): Model => {
  const mat = o?.material ?? 2;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 420);
  const { g } = c;
  const wr = mulberry32(4200 + mat * 3 + wear);
  const st = mat === 1 ? stone('#8f8a80') : mat === 3 ? stone('#9a9ea6') : stone('#c3beb4');
  const dk = darkOf(mat);
  const dim = [1, 0.65, 0.3][wear] as number;
  const oct = (r: number, h: number, y: number, m: THREE.Material): THREE.Mesh => {
    const s = M(new THREE.CylinderGeometry(r, r + 0.02, h, 8), m, g);
    s.position.y = y;
    s.rotation.y = Math.PI / 8;
    return s;
  };
  oct(0.6, 0.08, 0.04, dk);
  oct(0.52, 0.08, 0.12, st);
  oct(0.46, 0.04, 0.18, st);
  // The crimson runner up the front steps.
  const run = box(g, CRIMSON, 0.16, 0.012, 0.2, 0, 0.165, 0.45);
  run.rotation.x = -0.35;
  box(g, CRIMSON, 0.16, 0.006, 0.25, 0, 0.203, 0.25);

  // Columns.
  const N = 8;
  const CR = 0.36, Y0 = 0.2, CH = 0.6;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * TAU + TAU / 16;
    const x = Math.sin(a) * CR, z = Math.cos(a) * CR;
    if (mat === 1) {
      limb(g, V(x, Y0, z), V(x, Y0 + CH, z), 0.035, 0.032, WOOD, 6);
      box(g, DWOOD, 0.08, 0.04, 0.08, x, Y0 + CH, z, a);
      continue;
    }
    const broken = wear === 2 && i === 2;
    const h = broken ? 0.22 : CH;
    M(new THREE.CylinderGeometry(0.04, 0.045, h, 8), st, g).position.set(x, Y0 + h / 2, z);
    box(g, st, 0.08, 0.025, 0.08, x, Y0 + 0.012, z, a);
    if (broken) {
      snapTop(g, x, Y0 + h, z, 0.04, st, wr);
      for (const k of [0, 1]) {
        const d = M(new THREE.CylinderGeometry(0.04, 0.04, 0.17, 8), st, g);
        d.position.set(x * 1.45 + k * 0.12, 0.07 + k * 0.05, z * 1.45 + 0.08);
        d.rotation.set(0, a + k, Math.PI / 2);
      }
      continue;
    }
    if (wear === 1 && i === 2) box(g, st, 0.05, 0.03, 0.08, x - 0.015, Y0 + CH - 0.01, z, a);
    else box(g, st, 0.09, 0.03, 0.09, x, Y0 + CH - 0.005, z, a);
    if (mat === 3) for (const y of [Y0 + 0.12, Y0 + 0.45]) ring(g, 0.047, 0.008, IRON_BAND, y, 3, 10).position.set(x, y, z);
  }
  // Crimson hangings between the side columns (the Order's standard, its silver-white emblem).
  for (const k of [-1, 1]) {
    const a = k * (TAU / 4);
    const hx = Math.sin(a) * (CR + 0.005), hz = Math.cos(a) * (CR + 0.005);
    const hang = box(g, CRIMSON, 0.012, 0.36, 0.16, hx, Y0 + CH - 0.2, hz);
    hang.rotation.y = 0;
    const em = box(g, EMBLEM, 0.016, 0.05, 0.05, hx + k * 0.002, Y0 + CH - 0.16, hz);
    em.rotation.x = Math.PI / 4;
  }
  // The ring beam (entablature) and the dome.
  const top = G(g, 0, Y0 + CH, 0);
  const rb = M(new THREE.CylinderGeometry(CR + 0.07, CR + 0.07, 0.07, 8), mat === 1 ? DWOOD : st, top);
  rb.position.y = 0.035;
  rb.rotation.y = Math.PI / 8;
  const domeMat = mat === 1 ? SHINGLE : mat === 3 ? metal('#5d646e', { roughness: 0.45, metalness: 0.55, flatShading: true }) : metal('#9aa3ae', { roughness: 0.55, metalness: 0.4, flatShading: true });
  const dome = M(new THREE.SphereGeometry(CR + 0.05, 12, 6, 0, TAU, 0, Math.PI / 2), domeMat, top);
  dome.position.y = 0.07;
  dome.scale.y = 0.85;
  if (mat === 3) {
    for (let i = 0; i < 8; i++) {
      const rib = M(new THREE.TorusGeometry(CR + 0.055, 0.009, 3, 10, Math.PI / 2), IRON_BAND, top);
      rib.position.y = 0.07;
      rib.rotation.set(0, (i / 8) * TAU, Math.PI / 2);
      rib.scale.set(0.85, 1, 1);
      rib.rotation.order = 'YXZ';
    }
  }
  if (mat === 1) for (let i = 1; i < 4; i++) ring(top, (CR + 0.05) * Math.cos((i / 4) * (Math.PI / 2)) + 0.005, 0.008, DWOOD, 0.07 + (CR + 0.05) * 0.85 * Math.sin((i / 4) * (Math.PI / 2)), 3, 16);
  // The spire and the silver star of the Vigil.
  const sp = 0.07 + (CR + 0.05) * 0.85;
  limb(top, V(0, sp - 0.02, 0), V(0, sp + 0.2, 0), 0.03, 0.01, SILVER, 6);
  const star = G(top, 0, sp + 0.27, 0);
  if (wear === 2) {
    star.position.set(0.5, -Y0 - CH + 0.05, 0.25);
    star.rotation.set(Math.PI / 2 - 0.2, 0, 0.4);
  }
  for (const r of [0, Math.PI / 4]) {
    const a = box(star, VIGIL_LIT, 0.16, 0.025, 0.012, 0, 0, 0);
    a.rotation.z = r;
    const b = box(star, VIGIL_LIT, 0.025, 0.16, 0.012, 0, 0, 0);
    b.rotation.z = r;
  }
  if (wear < 2) glowSprite(top, VIGIL, 0.55, V(0, sp + 0.27, 0), 0.7);

  // The altar inside and the Vigil's flame on it.
  box(g, stone('#d6d3cc'), 0.2, 0.2, 0.14, 0, Y0 + 0.1, 0);
  box(g, CRIMSON, 0.21, 0.01, 0.08, 0, Y0 + 0.205, 0.0);
  box(g, CRIMSON, 0.08, 0.1, 0.006, 0, Y0 + 0.15, 0.073);
  const bowl = M(new THREE.CylinderGeometry(0.06, 0.035, 0.04, 8), SILVER, g);
  bowl.position.y = Y0 + 0.23;
  if (wear < 2) flame(g, V(0, Y0 + 0.25, 0), 0.5, wr, VIGIL);
  glowSprite(g, VIGIL, 0.75 * dim, V(0, Y0 + 0.33, 0), 0.75 * dim);
  // A censer hanging from the ring beam over the steps.
  limb(g, V(0, Y0 + CH, CR * 0.75), V(0, Y0 + CH - 0.18, CR * 0.75), 0.003, 0.003, STRING, 3);
  M(new THREE.SphereGeometry(0.03, 8, 5), SILVER, g).position.set(0, Y0 + CH - 0.2, CR * 0.75);

  if (wear > 0) {
    for (const [ry, y] of [[0, 0.12], [Math.PI / 2, 0.12], [-Math.PI * 0.75, 0.12]] as const) {
      crack(g, Math.sin(ry) * 0.53, y, Math.cos(ry) * 0.53, ry, wear === 2 ? 0.22 : 0.15, wr);
    }
    stain(top, 0.15, 0.06, CR + 0.075, 0, 0.06, wr);
    stain(top, -0.2, 0.06, -(CR + 0.075), Math.PI, 0.06, wr);
    rubbleAround(g, wear === 2 ? 10 : 5, 0.6, wr, rubbleColors(mat));
    if (mat === 1) splinters(g, wear === 2 ? 6 : 3, 0.55, wr);
  }
  return done(c, wear === 2 ? 1.2 : 1.55);
};

/* ---------- the trebuchet ---------- */

/**
 * The trebuchet: a heavy timber sled, two A-frames braced to it and an axle on top; the long throwing arm turns on
 * it ('arm', posed by the battle: cocked at rest, its long end down at the back, the sling and its boulder in the
 * trough; it swings up and over when it throws), a counterweight box on its short end. A winch and its crewman at
 * the back, a loader with a stone. Material: plain timber and a box of rocks (1), stone footing blocks and a carved
 * stone counterweight (2), iron straps on every beam and an iron counterweight, riveted (3). Level 3 hangs the
 * Order's pennant from the frame; level 4 doubles the beam with iron and stacks boulders by it. Worn (1): soot,
 * splinters, a brace gone. Crumbling (2): one A-frame leg snapped, the sled split, splinters everywhere.
 */
export const trebuchet = (o?: TrebuchetOpts): Model => {
  const mat = o?.material ?? 1;
  const level = o?.level ?? 1;
  const wear = o?.wear ?? 0;
  const c = begin(o, 0.75, 430);
  const { g } = c;
  const wr = mulberry32(4300 + mat * 3 + wear + level * 11);
  const iron = mat === 3;
  const strap = (a: THREE.Vector3, b: THREE.Vector3, t: number): void => {
    if (!iron) return;
    const p = a.clone().lerp(b, t);
    box(g, IRON_BAND, 0.06, 0.06, 0.07, p.x, p.y, p.z);
  };
  const H = 0.95;
  const Z = 0.2;
  // The sled: two long runners and cross-beams.
  for (const z of [-Z - 0.04, Z + 0.04]) {
    const r = box(g, WOOD, 1.1, 0.08, 0.08, 0, 0.06, z);
    if (wear === 2 && z > 0) {
      r.scale.x = 0.55;
      r.position.x = -0.25;
      const half = box(g, WOOD, 0.5, 0.08, 0.08, 0.32, 0.05, z + 0.06);
      half.rotation.y = 0.2;
      snapTop(g, 0.03, 0.06, z, 0.04, WOOD, wr);
    }
    if (mat >= 2) for (const x of [-0.45, 0.45]) box(g, stone(mat === 3 ? IRON_STONE : '#8f8a80'), 0.14, 0.05, 0.14, x, 0.0, z);
  }
  for (const x of [-0.5, -0.15, 0.2, 0.5]) box(g, DWOOD, 0.07, 0.06, Z * 2 + 0.2, x, 0.11, 0);
  // The two A-frames, braced fore and aft, with the axle on top.
  for (const z of [-Z, Z]) {
    const legF = [V(-0.38, 0.12, z), V(0, H, z)] as const;
    const legB = [V(0.38, 0.12, z), V(0, H, z)] as const;
    beam(g, legF[0], legF[1], 0.07, WOOD);
    if (wear === 2 && z > 0) {
      beam(g, legB[0], legB[0].clone().lerp(legB[1], 0.45), 0.07, WOOD);
      const p = legB[0].clone().lerp(legB[1], 0.45);
      snapTop(g, p.x, p.y, p.z, 0.035, WOOD, wr);
      // Its upper half lies against the sled.
      beam(g, V(0.42, 0.14, z + 0.18), V(0.05, 0.2, z + 0.32), 0.06, WOOD);
    } else beam(g, legB[0], legB[1], 0.07, WOOD);
    if (!(wear >= 1 && z > 0)) beam(g, V(-0.25, 0.42, z), V(0.25, 0.42, z), 0.045, DWOOD);
    // Raking struts out to the runners.
    beam(g, V(0, 0.12, z + Math.sign(z) * 0.24), V(0, H * 0.62, z), 0.04, DWOOD);
    strap(legF[0], legF[1], 0.25);
    strap(legB[0], legB[1], 0.25);
    strap(legF[0], legF[1], 0.92);
    if (iron) box(g, IRON_PLATE, 0.16, 0.12, 0.015, 0, H - 0.02, z + Math.sign(z) * 0.04);
  }
  const axle = M(new THREE.CylinderGeometry(0.03, 0.03, Z * 2 + 0.16, 8), DSTEEL, g);
  axle.rotation.x = Math.PI / 2;
  axle.position.y = H;
  if (level >= 2 || mat >= 2) {
    for (const z of [-Z - 0.08, Z + 0.08]) {
      const w = M(new THREE.CylinderGeometry(0.045, 0.045, 0.02, 8), DSTEEL, g);
      w.rotation.x = Math.PI / 2;
      w.position.set(0, H, z);
    }
  }

  // The arm (cocked at rest: the long end down at the back, +X).
  const arm = part(G(g, 0, H, 0), 'arm');
  const L = 1.12, S = 0.34;
  limb(arm, V(-S, 0, 0), V(L, 0, 0), 0.04, 0.022, WOOD, 6);
  if (level >= 4) {
    limb(arm, V(-S + 0.06, 0.045, 0), V(L * 0.7, 0.03, 0), 0.016, 0.012, DSTEEL, 5);
    for (const x of [-0.1, 0.25, 0.6]) box(arm, DSTEEL, 0.03, 0.09, 0.07, x, 0.01, 0);
  }
  if (iron) for (const x of [0.05, 0.45, 0.85]) box(arm, IRON_BAND, 0.035, 0.07, 0.065, x, 0, 0);
  // The counterweight on its short end: a box of rocks, a carved stone or an iron block.
  const cw = G(arm, -S, 0, 0);
  const cs = level >= 4 ? 0.3 : level >= 2 ? 0.26 : 0.23;
  for (const k of [-1, 1]) limb(cw, V(0, 0, k * 0.05), V(0, -0.12, k * (cs / 2 - 0.02)), 0.012, 0.012, DSTEEL, 4);
  if (mat === 1) {
    box(cw, DWOOD, cs, cs * 0.8, cs, 0, -0.12 - cs * 0.4, 0);
    for (let i = 0; i < 5; i++) {
      const r = M(new THREE.DodecahedronGeometry(0.05 + wr() * 0.02, 0), stone('#6d6559'), cw);
      r.position.set((wr() - 0.5) * cs * 0.6, -0.12 + 0.02, (wr() - 0.5) * cs * 0.6);
    }
  } else if (mat === 2) {
    box(cw, stone('#7d786f'), cs, cs * 0.8, cs, 0, -0.12 - cs * 0.4, 0);
    box(cw, DSTEEL, cs + 0.02, 0.025, cs + 0.02, 0, -0.14, 0);
  } else {
    box(cw, IRON_PLATE, cs, cs * 0.8, cs, 0, -0.12 - cs * 0.4, 0);
    for (const y of [-0.15, -0.12 - cs * 0.75]) box(cw, IRON_BAND, cs + 0.02, 0.03, cs + 0.02, 0, y, 0);
    for (const x of [-1, 1]) for (const z of [-1, 1]) M(new THREE.OctahedronGeometry(0.015, 0), IRON_RIVET, cw).position.set(x * cs * 0.35, -0.12 - cs * 0.4, z * (cs / 2 + 0.004));
  }
  // The sling at the long end, and its boulder.
  limb(arm, V(L, 0, 0), V(L - 0.02, -0.1, 0.03), 0.004, 0.004, STRING, 3);
  limb(arm, V(L, 0, 0), V(L - 0.02, -0.1, -0.03), 0.004, 0.004, STRING, 3);
  const stoneB = M(new THREE.DodecahedronGeometry(0.08, 0), stone('#6d6559'), arm);
  stoneB.position.set(L - 0.03, -0.14, 0);
  arm.rotation.z = -0.95;

  // The winch at the back and its crewman, a loader with a stone at the front.
  const winch = M(new THREE.CylinderGeometry(0.05, 0.05, Z * 2 + 0.1, 8), DWOOD, g);
  winch.rotation.x = Math.PI / 2;
  winch.position.set(0.48, 0.2, 0);
  for (const z of [-Z - 0.06, Z + 0.06]) {
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI;
      const sp = box(g, WOOD, 0.22, 0.02, 0.02, 0.48, 0.2, z);
      sp.rotation.z = a;
    }
  }
  limb(g, V(0.48, 0.24, 0), V(0.62, 0.08, 0), 0.006, 0.006, STRING, 3);
  const crew = part(G(g, 0, 0, 0), 'crew');
  crewman(crew, 0.72, 0, 0.36, -Math.PI / 2 - 0.4, 'haul', 0.45);
  crewman(crew, -0.55, 0, 0.42, Math.PI * 0.85, 'carry', 0.45, '#6e1f24');
  // The trough for the sling under the arm's long end.
  box(g, DWOOD, 0.4, 0.04, 0.12, 0.62, 0.11, 0).rotation.y = 0;

  if (level >= 3) pennant(g, -0.42, 0.1, -Z - 0.08, 1.0);
  if (level >= 4) {
    // A stack of boulders by the frame.
    const pile = G(g, -0.45, 0.04, Z + 0.25);
    for (const [x, y, z] of [[0, 0.06, 0], [0.13, 0.06, 0.02], [0.06, 0.16, 0.01], [-0.06, 0.05, 0.11]] as const) M(new THREE.DodecahedronGeometry(0.07, 0), stone('#5f574d'), pile).position.set(x, y, z);
  }
  if (wear > 0) {
    for (let i = 0; i < wear + 1; i++) stain(g, (wr() - 0.5) * 0.8, 0.101, (wr() - 0.5) * 0.3, 0, 0.07 + wr() * 0.04, wr, '#2e241b', true);
    splinters(g, wear === 2 ? 10 : 4, 0.6, wr);
    if (mat >= 2) rubbleAround(g, wear === 2 ? 6 : 3, 0.62, wr, rubbleColors(mat));
  }
  const t = (n: number): void => {
    // Cocked, then a throw every 24 steps: the arm whips up and over and winds back down.
    const f = n % 24;
    arm.rotation.z = f === 0 ? -0.95 : f < 3 ? 2.0 : 2.0 - ((f - 2) / 22) * 2.95;
  };
  t(0);
  return done(c, 1.5, t);
};

/* ---------- ruins ---------- */

/** What a destroyed watchtower, sanctum or trebuchet leaves, in its material. */
export const era4Ruin = (type: string, mat: Era2Material = 2): Model => {
  const c = begin(undefined, 0.7, 440);
  const { g } = c;
  const rng = mulberry32(4400 + type.length * 13 + mat * 5);
  const st = type === 'sanctum' ? (mat === 1 ? stone('#8f8a80') : mat === 3 ? stone('#9a9ea6') : stone('#c3beb4')) : stoneOf(mat);
  const colors = rubbleColors(mat);
  const scorch = (r: number, sx = 1): void => {
    const d = M(new THREE.CircleGeometry(r, 12), std('#1e1a16', { roughness: 1, bumpScale: 0 }), g);
    d.rotation.x = -Math.PI / 2;
    d.scale.x = sx;
    d.position.y = 0.006;
  };
  const charred = std('#2b2119', { roughness: 1 });
  let h: number;
  if (type === 'watchtower') {
    scorch(0.55);
    if (mat === 1) {
      for (const [x, z] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
        const sh = 0.1 + rng() * 0.25;
        limb(g, V(x * 0.3, 0, z * 0.3), V(x * 0.29, sh, z * 0.29), 0.05, 0.045, rng() < 0.5 ? charred : WOOD, 4);
        snapTop(g, x * 0.29, sh, z * 0.29, 0.045, WOOD, rng);
      }
      for (let i = 0; i < 4; i++) beam(g, V((rng() - 0.5) * 0.8, 0.03, (rng() - 0.5) * 0.8), V((rng() - 0.5) * 0.8, 0.05, (rng() - 0.5) * 0.8), 0.05, rng() < 0.5 ? charred : WOOD);
      splinters(g, 10, 0.5, rng);
    } else {
      const s = M(new THREE.CylinderGeometry(0.36, 0.4, 0.3, 4), st, g);
      s.position.y = 0.15;
      s.rotation.y = Math.PI / 4;
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * TAU + rng() * 0.4;
        const sh = 0.05 + rng() * 0.15;
        box(g, st, 0.12, sh, 0.1, Math.sin(a) * 0.22, 0.3 + sh / 2, Math.cos(a) * 0.22, a);
      }
      if (mat === 3) {
        const band = M(new THREE.TorusGeometry(0.22, 0.014, 4, 12, Math.PI * 0.9), IRON_BAND, g);
        band.position.set(0.35, 0.03, -0.3);
        band.rotation.set(-Math.PI / 2 + 0.2, 0, 1.1);
      }
      rubbleAround(g, 14, 0.55, rng, colors);
    }
    // The roof fallen on its side, the cold lantern beside it.
    const roof = M(new THREE.ConeGeometry(0.3, 0.28, 4), mat === 1 ? SHINGLE : SLATE, g);
    roof.position.set(0.42, 0.14, 0.32);
    roof.rotation.set(0.2, 0.5, Math.PI / 2 + 0.3);
    box(g, SILVER, 0.1, 0.1, 0.1, -0.35, 0.05, 0.38, 0.6);
    h = mat === 1 ? 0.35 : 0.45;
  } else if (type === 'sanctum') {
    const dk = darkOf(mat);
    const s0 = M(new THREE.CylinderGeometry(0.6, 0.62, 0.08, 8), dk, g);
    s0.position.y = 0.04;
    const s1 = M(new THREE.CylinderGeometry(0.5, 0.52, 0.08, 8), st, g);
    s1.position.y = 0.12;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + TAU / 16;
      const sh = [0.3, 0.06, 0.14, 0.04, 0.22, 0.08, 0.05, 0.18][i] as number;
      const x = Math.sin(a) * 0.36, z = Math.cos(a) * 0.36;
      if (mat === 1) limb(g, V(x, 0.16, z), V(x, 0.16 + sh, z), 0.035, 0.033, rng() < 0.5 ? charred : WOOD, 6);
      else M(new THREE.CylinderGeometry(0.04, 0.045, sh, 8), st, g).position.set(x, 0.16 + sh / 2, z);
      snapTop(g, x, 0.16 + sh, z, 0.04, mat === 1 ? WOOD : st, rng);
    }
    // Shards of the dome, the altar split, the star in the rubble.
    const domeMat = mat === 1 ? SHINGLE : mat === 3 ? metal('#5d646e', { roughness: 0.45, metalness: 0.55, flatShading: true }) : metal('#9aa3ae', { roughness: 0.55, metalness: 0.4, flatShading: true });
    for (let i = 0; i < 3; i++) {
      const sh = M(new THREE.SphereGeometry(0.3, 6, 3, rng() * TAU, 1.0, 0, 0.9), domeMat, g);
      sh.position.set((rng() - 0.5) * 0.7, 0.05, (rng() - 0.5) * 0.7);
      sh.rotation.set(rng() * 2, rng() * 3, 0);
    }
    box(g, stone('#d6d3cc'), 0.2, 0.1, 0.14, 0, 0.21, 0);
    const half = box(g, stone('#d6d3cc'), 0.1, 0.1, 0.14, 0.2, 0.2, 0.1, 0.6);
    half.rotation.z = 0.5;
    const star = G(g, -0.3, 0.03, 0.4);
    star.rotation.x = -Math.PI / 2;
    for (const r of [0, Math.PI / 4]) box(star, SILVER, 0.14, 0.02, 0.01, 0, 0, 0).rotation.z = r;
    rubbleAround(g, 12, 0.6, rng, colors);
    h = 0.48;
  } else {
    // The trebuchet: the sled charred, the arm broken across it, the counterweight spilled.
    scorch(0.6, 1.3);
    const bed = box(g, WOOD, 1.0, 0.08, 0.5, 0, 0.1, 0);
    bed.rotation.set(0.05, 0.25, 0.1);
    beam(g, V(-0.55, 0.06, -0.2), V(0.2, 0.3, 0.1), 0.06, charred);
    beam(g, V(0.1, 0.32, 0.1), V(0.7, 0.04, 0.3), 0.06, WOOD);
    beam(g, V(-0.2, 0.05, 0.35), V(0.4, 0.06, 0.5), 0.07, rng() < 0.5 ? charred : WOOD);
    const cw = box(g, mat === 3 ? IRON_PLATE : mat === 2 ? stone('#7d786f') : DWOOD, 0.24, 0.2, 0.24, -0.5, 0.1, 0.3, 0.4);
    cw.rotation.z = 0.3;
    for (let i = 0; i < 4; i++) M(new THREE.DodecahedronGeometry(0.06, 0), stone('#6d6559'), g).position.set(-0.35 + rng() * 0.4, 0.05, 0.45 + rng() * 0.2);
    splinters(g, 12, 0.6, rng);
    if (mat >= 2) rubbleAround(g, 6, 0.6, rng, colors);
    h = 0.35;
  }
  return done(c, h);
};
